<template>
    <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css" />
    <div v-if="editor" class="container w-auto">
        <div class="control-group mb-4">
            <div
                class="button-group toolbar flex flex-wrap gap-2 bg-gray-100 p-2 rounded-md shadow border border-gray-200"
            >
                <!-- Bold, Italic, Strike -->
                <button
                    @click="editor.chain().focus().toggleBold().run()"
                    :disabled="!editor.can().chain().focus().toggleBold().run()"
                    :class="['toolbar-button', editor.isActive('bold') ? 'active' : '']"
                >
                    <i class="fas fa-bold"></i>
                </button>
                <button
                    @click="editor.chain().focus().toggleItalic().run()"
                    :disabled="!editor.can().chain().focus().toggleItalic().run()"
                    :class="['toolbar-button', editor.isActive('italic') ? 'active' : '']"
                >
                    <i class="fas fa-italic"></i>
                </button>
                <button
                    @click="editor.chain().focus().toggleStrike().run()"
                    :disabled="!editor.can().chain().focus().toggleStrike().run()"
                    :class="['toolbar-button', editor.isActive('strike') ? 'active' : '']"
                >
                    <i class="fas fa-strikethrough"></i>
                </button>

                <!-- Text Color and Highlight Color -->
                <button @click="openTextColorPanel($event)" class="toolbar-button">
                    <i class="fas fa-palette"></i>
                </button>
                <button @click="openHighlightColorPanel($event)" class="toolbar-button">
                    <i class="fas fa-highlighter"></i>
                </button>

                <!-- Bullet List, Ordered List, Blockquote -->
                <button
                    @click="editor.chain().focus().toggleBulletList().run()"
                    :class="['toolbar-button', editor.isActive('bulletList') ? 'active' : '']"
                >
                    <i class="fas fa-list-ul"></i>
                </button>
                <button
                    @click="editor.chain().focus().toggleOrderedList().run()"
                    :class="['toolbar-button', editor.isActive('orderedList') ? 'active' : '']"
                >
                    <i class="fas fa-list-ol"></i>
                </button>
                <button
                    @click="toggleBlockquote"
                    :class="['toolbar-button', editor.isActive('blockquote') ? 'active' : '']"
                >
                    <i class="fas fa-quote-right"></i>
                </button>

                <!-- Link, Image, Video with OverlayPanel -->
                <button @click="openLinkPanel($event)" class="toolbar-button">
                    <i class="fas fa-link"></i>
                </button>
                <button @click="openVideoPanel($event)" class="toolbar-button">
                    <i class="fab fa-youtube"></i>
                </button>
                <button @click="openVimeoPanel($event)" class="toolbar-button">
                    <i class="fab fa-vimeo-v"></i>
                </button>
                <button @click="openLoomPanel($event)" class="toolbar-button !p-1.5">
                    <LoomIcon class="text-gray-500" />
                </button>
                <button @click="openImagePanel($event)" class="toolbar-button">
                    <i class="fas fa-image"></i>
                </button>

                <!-- Link Overlay Panel -->
                <OverlayPanel ref="linkPanel" class="border border-gray-300 shadow-md rounded-md">
                    <div class="p-4">
                        <label class="font-semibold mb-2 block">Enter Link URL:</label>
                        <input v-model="linkUrl" type="text" class="modal-input" />
                        <div class="modal-buttons">
                            <button @click="confirmLink" class="modal-button primary-button w-full">Confirm</button>
                            <button
                                @click="closeOverlayPanel('linkPanel')"
                                class="modal-button secondary-button w-full"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </OverlayPanel>

                <!-- YouTube Video Overlay Panel -->
                <OverlayPanel ref="videoPanel" class="border border-gray-300 shadow-md rounded-md">
                    <div class="p-4">
                        <label class="font-semibold mb-2 block">Enter YouTube Link:</label>
                        <input
                            v-model="videoUrl"
                            type="text"
                            class="modal-input"
                            placeholder="https://www.youtube.com/... or https://youtu.be/..."
                        />
                        <div class="modal-buttons">
                            <button @click="confirmYouTubeVideo" class="modal-button primary-button w-full">
                                Confirm
                            </button>
                            <button
                                @click="closeOverlayPanel('videoPanel')"
                                class="modal-button secondary-button w-full"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </OverlayPanel>

                <!-- Vimeo Video Overlay Panel -->
                <OverlayPanel ref="vimeoPanel" class="border border-gray-300 shadow-md rounded-md">
                    <div class="p-4">
                        <label class="font-semibold mb-2 block">Enter Vimeo Link:</label>
                        <input v-model="vimeoUrl" type="text" class="modal-input" placeholder="https://vimeo.com/..." />
                        <div class="modal-buttons">
                            <button @click="confirmVimeoVideo" class="modal-button primary-button w-full">
                                Confirm
                            </button>
                            <button
                                @click="closeOverlayPanel('vimeoPanel')"
                                class="modal-button secondary-button w-full"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </OverlayPanel>

                <!-- Image Overlay Panel -->
                <OverlayPanel ref="imagePanel" class="border border-gray-300 shadow-md rounded-md">
                    <div class="p-4">
                        <div class="mb-4">
                            <label class="font-semibold mb-2 block">Upload Image:</label>
                            <input type="file" @change="handleImageSelection" accept="image/*" class="modal-input" />
                            <div v-if="selectedImage" class="mt-2">
                                <div
                                    class="inline-flex items-center px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                                >
                                    <i class="fas fa-paperclip mr-2 text-gray-500"></i>
                                    <span class="truncate max-w-[200px]">{{ selectedImage.name }}</span>
                                    <button
                                        @click="clearSelectedImage"
                                        class="ml-2 p-1 hover:bg-gray-200 rounded-full transition-colors"
                                    >
                                        <i class="fas fa-times text-gray-500"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div class="mb-4">
                            <label class="font-semibold mb-2 block">Link URL (optional):</label>
                            <input
                                v-model="imageLinkUrl"
                                type="text"
                                placeholder="Enter URL to make image clickable"
                                class="modal-input"
                            />
                        </div>
                        <div class="modal-buttons">
                            <button
                                @click="handleImageWithLink"
                                class="modal-button primary-button w-full"
                                :disabled="!selectedImage"
                            >
                                Add Image
                            </button>
                            <button @click="closeImagePanel" class="modal-button secondary-button w-full">
                                Cancel
                            </button>
                        </div>
                    </div>
                </OverlayPanel>

                <!-- Loom Video Overlay Panel -->
                <OverlayPanel ref="loomPanel" class="border border-gray-300 shadow-md rounded-md">
                    <div class="p-4">
                        <label class="font-semibold mb-2 block">Enter Loom Link:</label>
                        <input
                            v-model="loomUrl"
                            type="text"
                            class="modal-input"
                            placeholder="https://www.loom.com/share/..."
                        />
                        <div class="modal-buttons">
                            <button @click="confirmLoomVideo" class="modal-button primary-button w-full">
                                Confirm
                            </button>
                            <button
                                @click="closeOverlayPanel('loomPanel')"
                                class="modal-button secondary-button w-full"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </OverlayPanel>

                <!-- Text Color Overlay Panel -->
                <OverlayPanel ref="textColorPanel" class="border border-gray-300 shadow-md rounded-md z-50">
                    <div class="p-4">
                        <label class="font-semibold mb-2 block">Select Text Color</label>
                        <div class="flex flex-wrap gap-2 mb-4">
                            <button
                                v-for="color in predefinedColors"
                                :key="color"
                                class="w-8 h-8 rounded-md border border-gray-200 cursor-pointer hover:border-gray-400 transition-colors"
                                :style="{ backgroundColor: color }"
                                @click="applyTextColorDirect(color)"
                            ></button>
                        </div>
                        <div class="flex items-center gap-4">
                            <div class="flex-1">
                                <label class="text-sm text-gray-600 mb-1 block">Custom Color</label>
                                <ColorPicker v-model="selectedTextColor" class="w-full" :inline="true" format="hex" />
                            </div>
                            <div class="flex flex-col gap-2">
                                <button @click="applyTextColor" class="modal-button primary-button">Apply</button>
                                <button @click="clearTextColor" class="modal-button warning-button">Clear</button>
                                <button
                                    @click="closeOverlayPanel('textColorPanel')"
                                    class="modal-button secondary-button"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </OverlayPanel>

                <!-- Highlight Color Overlay Panel -->
                <OverlayPanel ref="highlightColorPanel" class="border border-gray-300 shadow-md rounded-md z-50">
                    <div class="p-4">
                        <label class="font-semibold mb-2 block">Select Highlight Color</label>
                        <div class="flex flex-wrap gap-2 mb-4">
                            <button
                                v-for="color in predefinedHighlightColors"
                                :key="color"
                                class="w-8 h-8 rounded-md border border-gray-200 cursor-pointer hover:border-gray-400 transition-colors"
                                :style="{ backgroundColor: color }"
                                @click="applyHighlightColorDirect(color)"
                            ></button>
                        </div>
                        <div class="flex items-center gap-4">
                            <div class="flex-1">
                                <label class="text-sm text-gray-600 mb-1 block">Custom Color</label>
                                <ColorPicker
                                    v-model="selectedHighlightColor"
                                    class="w-full"
                                    :inline="true"
                                    format="hex"
                                />
                            </div>
                            <div class="flex flex-col gap-2">
                                <button @click="applyHighlightColor" class="modal-button primary-button">Apply</button>
                                <button @click="clearHighlightColor" class="modal-button warning-button">Clear</button>
                                <button
                                    @click="closeOverlayPanel('highlightColorPanel')"
                                    class="modal-button secondary-button"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </OverlayPanel>

                <!-- Alignment -->
                <button @click="editor.chain().focus().setTextAlign('left').run()" class="toolbar-button">
                    <i class="fas fa-align-left"></i>
                </button>
                <button @click="editor.chain().focus().setTextAlign('center').run()" class="toolbar-button">
                    <i class="fas fa-align-center"></i>
                </button>
                <button @click="editor.chain().focus().setTextAlign('right').run()" class="toolbar-button">
                    <i class="fas fa-align-right"></i>
                </button>

                <!-- Undo, Redo -->
                <button
                    @click="editor.chain().focus().undo().run()"
                    :disabled="!editor.can().chain().focus().undo().run()"
                    class="toolbar-button"
                >
                    <i class="fas fa-undo"></i>
                </button>
                <button
                    @click="editor.chain().focus().redo().run()"
                    :disabled="!editor.can().chain().focus().redo().run()"
                    class="toolbar-button"
                >
                    <i class="fas fa-redo"></i>
                </button>

                <!-- Add Listings Button -->
                <button @click="openListingsPanel($event)" class="toolbar-button !p-1.5">
                    <HomeIcon class="text-gray-500" />
                </button>

                <!-- Listings Overlay Panel -->
                <OverlayPanel ref="listingsPanel" class="border border-gray-300 shadow-md rounded-md">
                    <div class="w-[445px] h-[503px] flex flex-col">
                        <!-- Header Section -->
                        <div class="flex items-center justify-between px-4 py-3 flex-shrink-0">
                            <h3 class="font-semibold text-base leading-6 text-gray-900">Select Listings to Add</h3>
                            <GgmsDropdown
                                v-model="sortBy"
                                :options="sortOptions"
                                placeholder="Sort by"
                                optionLabel="label"
                                optionValue="value"
                                class="border-0 rounded-md ring-0"
                                :small="true"
                                :isShowClear="true"
                            />
                        </div>

                        <GgmsSeparator class="flex-shrink-0" />

                        <!-- Content Section -->
                        <div class="flex-1 px-4 py-3 overflow-hidden relative">
                            <!-- Search Bar -->
                            <div class="relative mb-4">
                                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <SearchIcon class="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    v-model="searchQuery"
                                    type="text"
                                    placeholder="Search by Listing ID..."
                                    class="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-color-500 focus:border-primary-color-500 sm:text-sm"
                                    @input="onSearchInput"
                                />
                            </div>

                            <div v-if="isLoading" class="flex items-center justify-center h-full">
                                <i class="fas fa-spinner fa-spin text-primary-color-600 text-3xl"></i>
                            </div>

                            <!-- No results message - positioned to center in entire content area -->
                            <div
                                v-else-if="listings.length === 0 && searchQuery"
                                class="absolute inset-0 flex items-center justify-center"
                                style="top: 30px; pointer-events: none"
                            >
                                <div class="text-center" style="pointer-events: auto">
                                    <p class="text-gray-500 text-sm">No listings found for "{{ searchQuery }}"</p>
                                    <GgmsButton @click="clearSearch" styleType="secondary" class="mt-2">
                                        Clear search
                                    </GgmsButton>
                                </div>
                            </div>

                            <!-- Listings grid -->
                            <div v-else class="h-full flex flex-col">
                                <div class="flex-1 overflow-y-auto">
                                    <div class="grid grid-cols-1 gap-4">
                                        <div
                                            v-for="(listing, index) in listings"
                                            :key="listing.listingId"
                                            class="flex items-center gap-4 p-4 border rounded-lg hover:bg-gray-50 transition-shadow shadow-sm cursor-pointer"
                                            @click="toggleListingSelection(index)"
                                        >
                                            <img
                                                :src="
                                                    getValidPhotos(listing?.attributes?.media).length > 0
                                                        ? getValidPhotos(listing.attributes?.media)[0].mediaURL
                                                        : require('@/assets/icons/no_image.png')
                                                "
                                                alt="Listing Image"
                                                class="w-20 h-16 rounded-md object-cover flex-shrink-0"
                                                @error="
                                                    (event) =>
                                                        handleImageError(
                                                            event,
                                                            getValidPhotos(listing?.attributes?.media)
                                                        )
                                                "
                                            />
                                            <div class="flex-1 min-w-0">
                                                <h3 class="font-semibold text-base leading-6 text-gray-900">
                                                    {{ listing.listingId }}
                                                </h3>
                                                <p class="text-gray-900 text-sm leading-5 font-normal">
                                                    {{ listing.attributes?.city || "Coventry" }},
                                                    {{ listing.attributes?.country || "United Kingdom" }}
                                                </p>
                                                <div class="flex items-center gap-2 text-gray-900">
                                                    <span class="text-sm leading-5"
                                                        ><span class="font-semibold">{{
                                                            listing.attributes?.bedroomsTotal ?? 0
                                                        }}</span>
                                                        beds</span
                                                    >
                                                    <span class="text-sm leading-5"
                                                        ><span class="font-semibold">{{
                                                            listing.attributes?.rawMlsBathroomsTotal ?? 0
                                                        }}</span>
                                                        baths</span
                                                    >
                                                    <span class="text-sm leading-5"
                                                        ><span class="font-semibold">{{
                                                            numberWithCommas(
                                                                Number(listing.attributes?.lotSizeSquareFeet ?? 0)
                                                            )
                                                        }}</span>
                                                        sq.ft</span
                                                    >
                                                </div>
                                                <div class="flex items-center gap-2 text-sm text-gray-900">
                                                    <div class="flex items-center gap-1">
                                                        <EyeIcon class="w-4 h-4" />
                                                        <span class="text-sm leading-5 font-normal">{{
                                                            listing.totalViewsCount ?? 0
                                                        }}</span>
                                                    </div>
                                                    <div class="flex items-center gap-1">
                                                        <HeartIcon class="w-4 h-4" />
                                                        <span class="text-sm leading-5 font-normal">{{
                                                            listing.totalFavouritesCount ?? 0
                                                        }}</span>
                                                    </div>
                                                    <div class="flex items-center gap-1">
                                                        <EyeOffIcon class="w-4 h-4" />
                                                        <span class="text-sm leading-5 font-normal">{{
                                                            listing.totalHiddenCount ?? 0
                                                        }}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <!-- Custom GgmsCheckBox with independent state tracking and click stop propagation -->
                                            <GgmsCheckBox
                                                :modelValue="isListingSelected(listing)"
                                                :label="''"
                                                @click.stop
                                                @update:modelValue="() => updateSelectedListings(index)"
                                                class="flex-shrink-0 mt-1"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Footer Section -->
                        <div class="bg-gray-50 px-4 py-3 flex-shrink-0 rounded-b-lg">
                            <div class="flex justify-end gap-x-3">
                                <GgmsButton styleType="secondary" @click="closeOverlayPanel('listingsPanel')">
                                    Cancel
                                </GgmsButton>
                                <GgmsButton @click="confirmListings">
                                    {{ buttonText }}
                                </GgmsButton>
                            </div>
                        </div>
                    </div>
                </OverlayPanel>

                <button @click="openMergeFieldsModal" class="toolbar-button">
                    <CurlyBracketsIcon class="h-5 w-5 text-gray-500" />
                </button>
                <!-- Add Merge Fields Button -->
                <GgmsMergeFields
                    :open="isMergeFieldsModalOpen"
                    :isForRichEditor="true"
                    :specialVariable="specialVariable"
                    :isTemplateTypeEmail="true"
                    @add-merge-field="addMergeFieldToTextMessage"
                    @closeModal="closeMergeFieldsModal"
                />
            </div>
        </div>
        <EditorContent :editor="editor" class="editor-content border border-gray-300 rounded-md shadow-sm min-h-48" />
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, defineProps, defineEmits, computed, onUnmounted } from "vue"

// Import Tiptap Editor and Extensions
import { Editor, EditorContent, type Editor as TiptapEditor } from "@tiptap/vue-3"
import { Color } from "@tiptap/extension-color"
import ListItem from "@tiptap/extension-list-item"
import Placeholder from "@tiptap/extension-placeholder"
import TextStyle from "@tiptap/extension-text-style"
import StarterKit from "@tiptap/starter-kit"
import TextAlign from "@tiptap/extension-text-align"
import Link from "@tiptap/extension-link"
import ResizableImage from "@/components/rich-editor-extensions/ResizableImage"
import Youtube from "@tiptap/extension-youtube"
import { Vimeo } from "@/components/rich-editor-extensions/Vimeo"
import ListingsBlock from "@/components/rich-editor-extensions/ListingsBlock"
import Highlight from "@tiptap/extension-highlight"

// Import Table Extensions
import Table from "@tiptap/extension-table"
import TableRow from "@tiptap/extension-table-row"
import TableCell from "@tiptap/extension-table-cell"
import TableHeader from "@tiptap/extension-table-header"

// Import PrimeVue Components
import OverlayPanel from "primevue/overlaypanel"
import ColorPicker from "primevue/colorpicker"

// Import Heroicons
import { HomeIcon, EyeIcon, SearchIcon, HeartIcon, EyeOffIcon } from "@heroicons/vue/outline"

// Import Stores
import { useListingStore } from "@/stores/listings"
import { useContactListingStore } from "@/stores/contact-listings"
import { useAgencyStore } from "@/stores/agency"

// Import Custom Components
import GgmsCheckBox from "@/components/GgmsCheckBox.vue"
import GgmsMergeFields from "@/components/GgmsMergeFields.vue"
import GgmsDropdown from "@/components/GgmsDropdown.vue"
import GgmsButton from "@/components/GgmsButton.vue"

// Import Models
import { Listing } from "@/shared/models/listing"
import GgmsSeparator from "@/components/GgmsSeparator.vue"
import { numberWithCommas, debounce, handleImageError, getDomainId } from "@/shared/utils/helpers"

// Import Loom Icon
import LoomIcon from "@/components/icons/LoomIcon.vue"
import CurlyBracketsIcon from "@/components/icons/CurlyBracketsIcon.vue"

// Import Loom extension
import { Loom } from "@/components/rich-editor-extensions/Loom"

// Import Loom utils
import { isValidLoomUrl } from "@/components/rich-editor-extensions/loom-utils"

// Filter media to only show photos
const getValidPhotos = (media: any[]) => {
    if (!media || !Array.isArray(media)) return []
    return media.filter((m) => m.mediaCategory === "Photo")
}

const props = defineProps({
    modelValue: {
        type: String,
        default: "",
    },
    errorMessage: {
        type: String,
        default: "",
    },
    placeholder: {
        type: String,
        default: "Write your email here...",
    },
    resetTrigger: {
        type: Number,
        default: 0,
    },
})

const emit = defineEmits(["update:modelValue", "disableValidationError"])

const listingsStore = useListingStore()
const contactListingsStore = useContactListingStore()
const agencyStore = useAgencyStore()

const editor = ref<TiptapEditor | null>(null)
const linkUrl = ref<string>("")
const videoUrl = ref<string>("")
const vimeoUrl = ref<string>("")
const loomUrl = ref<string>("")
const body = ref<string>(props.modelValue)

// sort by
const sortBy = ref<string>("")
const sortOptions = ref([
    { label: "Total Views", value: "totalViews" },
    { label: "Recently Viewed", value: "recentlyViewed" },
    { label: "Recently Saved", value: "recentlySaved" },
])
// listings
const selectedListings = ref<Listing[]>([])
const selectedListingIds = ref<Set<string>>(new Set())
const searchQuery = ref<string>("")

// merge fields
const specialVariable = ref("")

// Computed properties
const listings = computed(() => listingsStore.tableState.data)

// Refs for OverlayPanels
const linkPanel = ref<OverlayPanel | null>(null)
const videoPanel = ref<OverlayPanel | null>(null)
const vimeoPanel = ref<OverlayPanel | null>(null)
const imagePanel = ref<OverlayPanel | null>(null)
const listingsPanel = ref<OverlayPanel | null>(null)
const loomPanel = ref<OverlayPanel | null>(null)

// Agency
const agency = computed(() => agencyStore.selectedAgency)

// Loading
const isLoading = ref<boolean>(false)

// Computed property for button text based on selection state
const buttonText = computed(() => {
    const hasExistingListings = getAllListingsFromEditor().length > 0
    const hasSelectedListings = selectedListings.value.length > 0

    if (!hasSelectedListings && hasExistingListings) {
        return "Delete Block"
    } else if (hasSelectedListings && hasExistingListings) {
        return "Update Listings"
    } else {
        return "Add Listings"
    }
})

// Computed property to determine if a listing is selected based on its ID
const isListingSelected = (listing: Listing): boolean => {
    return selectedListingIds.value.has(listing.listingId)
}

// Function to extract all listings from existing blocks in the editor
function getAllListingsFromEditor(): Listing[] {
    if (!editor.value) return []

    const allListings: Listing[] = []
    const editorJSON = editor.value.getJSON()

    // Recursively search for listings blocks in the editor content
    function searchForListingsBlocks(content: any): void {
        if (Array.isArray(content)) {
            content.forEach(searchForListingsBlocks)
        } else if (content && typeof content === "object") {
            if (content.type === "listingsBlock" && content.attrs?.listings) {
                allListings.push(...content.attrs.listings)
            }
            if (content.content) {
                searchForListingsBlocks(content.content)
            }
        }
    }

    searchForListingsBlocks(editorJSON.content)
    return allListings
}

// Function to pre-select existing listings in the overlay
function preselectExistingListings(existingListings: Listing[]): void {
    if (existingListings.length === 0) return

    // Clear current selections
    selectedListingIds.value.clear()
    selectedListings.value = []

    // Add existing listings to selection
    existingListings.forEach((listing: Listing) => {
        selectedListingIds.value.add(listing.listingId)
        selectedListings.value.push(listing)
    })
}

function openListingsPanel(event: Event) {
    // Get all existing listings from blocks in the editor
    const existingListings = getAllListingsFromEditor()

    // Pre-select existing listings in the overlay
    preselectExistingListings(existingListings)

    listingsPanel.value?.toggle(event)
}

const isMergeFieldsModalOpen = ref(false)

function openMergeFieldsModal() {
    specialVariable.value = ""
    isMergeFieldsModalOpen.value = true
}

function closeMergeFieldsModal() {
    isMergeFieldsModalOpen.value = false
}

// Helper function to remove listings blocks from content
function removeListingsBlocks(content: any): any {
    if (Array.isArray(content)) {
        return content.map(removeListingsBlocks).filter((item) => item !== null)
    } else if (content && typeof content === "object") {
        if (content.type === "listingsBlock") {
            return null // Remove this block
        }
        if (content.content) {
            content.content = removeListingsBlocks(content.content)
        }
        return content
    }
    return content
}

function confirmListings() {
    const editorInstance = editor.value
    const hasExistingListings = getAllListingsFromEditor().length > 0
    const hasSelectedListings = selectedListings.value.length > 0

    if (!editorInstance) return

    // If no listings selected and there are existing listings, delete the block
    if (!hasSelectedListings && hasExistingListings) {
        // Find and delete existing listings blocks
        const editorJSON = editorInstance.getJSON()
        const updatedContent = removeListingsBlocks(editorJSON.content)
        editorInstance.commands.setContent({ type: "doc", content: updatedContent })

        resetSearchAndClosePanel()
        return
    }

    // If there are selected listings, insert or update the block
    if (hasSelectedListings) {
        // If there are existing listings, we need to replace them
        if (hasExistingListings) {
            // Remove existing blocks first
            const editorJSON = editorInstance.getJSON()
            const updatedContent = removeListingsBlocks(editorJSON.content)
            editorInstance.commands.setContent({ type: "doc", content: updatedContent })
        }

        // Insert the new listings block
        editorInstance.chain().focus().insertListingsBlock(selectedListings.value).run()
        resetSearchAndClosePanel()
    }
}

// Helper function to reset search and close panel
async function resetSearchAndClosePanel() {
    // Close panel - the closeOverlayPanel function will handle search reset if needed
    closeOverlayPanel("listingsPanel")
}

// Function to toggle listing selection
function toggleListingSelection(index: number) {
    const listing = listings.value[index]
    const isCurrentlySelected = selectedListingIds.value.has(listing.listingId)

    if (isCurrentlySelected) {
        // Remove from selection
        selectedListingIds.value.delete(listing.listingId)
        selectedListings.value = selectedListings.value.filter((item) => item.listingId !== listing.listingId)
    } else {
        // Add to selection
        selectedListingIds.value.add(listing.listingId)
        selectedListings.value.push(listing)
    }
}

// Update selected listings when checkboxes change
function updateSelectedListings(index: number) {
    toggleListingSelection(index)
}

// Search functionality
async function performSearch() {
    try {
        isLoading.value = true
        // Set the search value in the table state
        listingsStore.tableState.search = searchQuery.value
        listingsStore.tableState.listingIdSearch = true
        await listingsStore.getAll()
        // Don't reset selected state when search results change - keep existing selections
    } catch (error) {
        console.error("Error searching listings:", error)
    } finally {
        listingsStore.tableState.listingIdSearch = false
        isLoading.value = false
    }
}

// Debounced search function - waits 300ms after user stops typing
const debouncedSearch = debounce(performSearch, 300)

function onSearchInput() {
    // Call the debounced search function
    debouncedSearch()
}

async function clearSearch() {
    searchQuery.value = ""
    listingsStore.tableState.search = ""
    try {
        isLoading.value = true
        // Keep the current sortBy when clearing search
        listingsStore.tableState.sortBy = sortBy.value || undefined
        await listingsStore.getAll()
        // Don't reset selected state when clearing search - keep existing selections
    } catch (error) {
        console.error("Error clearing search:", error)
    } finally {
        isLoading.value = false
    }
}

// Watch for sortBy changes
watch(sortBy, async (newSortBy) => {
    try {
        isLoading.value = true
        listingsStore.tableState.sortBy = newSortBy || undefined
        await listingsStore.getAll()
        // Don't reset selected state when sort changes - keep existing selections
    } catch (error) {
        console.error("Error sorting listings:", error)
    } finally {
        isLoading.value = false
    }
})

function addMergeFieldToTextMessage(mergeField: string) {
    if (editor.value) {
        editor.value.chain().focus().insertContent(mergeField).run()
    }
}

// Synchronize body with parent component
watch(body, (newValue) => {
    emit("update:modelValue", newValue)
})

// Watch for changes in props and update body accordingly
watch(
    () => props.modelValue,
    (newValue) => {
        if (newValue !== body.value) {
            body.value = newValue
            if (editor.value) {
                editor.value.commands.setContent(newValue)
            }
        }
    }
)

// Function to reset selected listings overlay
function resetListingsOverlay() {
    selectedListingIds.value.clear()
    selectedListings.value = []
}

// Watch for reset trigger changes
watch(
    () => props.resetTrigger,
    (newValue, oldValue) => {
        if (newValue !== oldValue && newValue > 0) {
            resetListingsOverlay()
        }
    }
)

onUnmounted(() => {
    listingsStore.tableState.sortBy = ""
})

onMounted(async () => {
    try {
        isLoading.value = true
        editor.value = new Editor({
            content: body.value,
            extensions: [
                Color.configure({ types: [TextStyle.name, ListItem.name] }),
                TextStyle,
                StarterKit,
                Placeholder.configure({
                    placeholder: props.placeholder,
                }),
                TextAlign.configure({
                    types: ["heading", "paragraph"],
                }),
                Link.configure({
                    autolink: false,
                    openOnClick: true,
                }).extend({
                    inclusive: false,
                }),
                ResizableImage,
                Youtube.configure({
                    controls: true,
                    width: 640,
                    height: 360,
                    HTMLAttributes: {
                        class: "responsive-video",
                    },
                }),
                Vimeo.configure({
                    controls: true,
                    width: 640,
                    height: 360,
                    HTMLAttributes: {
                        class: "responsive-video",
                    },
                }),
                Table.configure({
                    resizable: true,
                }),
                TableRow,
                TableHeader,
                TableCell,
                ListingsBlock,
                Highlight.configure({
                    multicolor: true,
                    HTMLAttributes: {
                        class: "highlight",
                    },
                }),
                Loom,
            ],
            editorProps: {
                handlePaste: (view, event) => {
                    const text = event.clipboardData?.getData("text/plain")
                    const isYoutube =
                        text?.startsWith("https://www.youtube.com/watch?v=") || text?.startsWith("https://youtu.be/")
                    const isVimeo = text?.match(/https?:\/\/(www\.)?vimeo.com\/(?:video\/)?(\d+)/)
                    const isLoom = text && isValidLoomUrl(text)

                    if (text && (isYoutube || isVimeo || isLoom)) {
                        event.preventDefault()

                        if (isYoutube) {
                            editor.value?.commands.setYoutubeVideo({ src: text })
                        } else if (isVimeo) {
                            editor.value?.commands.setVimeoVideo({ src: text })
                        } else if (isLoom) {
                            editor.value?.commands.setLoomVideo({ src: text })
                        }
                        return true
                    }

                    // Check if the pasted text is a valid URL
                    const urlRegex = /^(https?:\/\/[^\s]+)$/
                    if (text && urlRegex.test(text) && !isYoutube) {
                        event.preventDefault() // Prevent the default paste behavior

                        // Insert the text as a clickable link
                        editor.value
                            ?.chain()
                            .focus()
                            .insertContent({
                                type: "text",
                                text: text,
                                marks: [
                                    {
                                        type: "link",
                                        attrs: {
                                            href: text,
                                            target: "_blank",
                                            rel: "noopener noreferrer",
                                        },
                                    },
                                ],
                            })
                            .run()

                        return true // Indicate the paste event was handled
                    }

                    if (text && (text.startsWith("http://") || text.startsWith("https://"))) {
                        event.preventDefault() // Prevent the default behavior
                        editor.value?.chain().focus().insertContent(text).run() // Insert as plain text
                        return true // Indicate the paste event is handled
                    }

                    return false // Let the default behavior handle non-video content
                },
            },
            onUpdate: ({ editor }) => {
                const editorHTML = editor.getHTML()
                // Generate the actual email HTML with listings converted
                const emailHTML = generateEmailHTML(editorHTML)
                body.value = emailHTML

                // Check if all listings blocks have been deleted and clear selections if so
                const currentListings = getAllListingsFromEditor()
                if (currentListings.length === 0 && selectedListings.value.length > 0) {
                    // All listings blocks have been deleted, clear selections
                    selectedListingIds.value.clear()
                    selectedListings.value = []
                }
            },
        })
        if (listingsStore.tableState.data.length === 0) {
            await listingsStore.getAll()
        }

        // Initialize empty selections
        selectedListingIds.value.clear()
        selectedListings.value = []

        // Only prefill if the editor is empty and there are selected listings
        if (
            contactListingsStore.tableState.selectedData &&
            contactListingsStore.tableState.selectedData.length > 0 &&
            !body.value &&
            editor.value
        ) {
            editor.value.chain().focus().insertListingsBlock(contactListingsStore.tableState.selectedData).run()
        }
        if (
            listingsStore.tableState.selectedData &&
            listingsStore.tableState.selectedData.length > 0 &&
            !body.value &&
            editor.value
        ) {
            editor.value.chain().focus().insertListingsBlock(listingsStore.tableState.selectedData).run()
        }
    } finally {
        isLoading.value = false
    }
})

// Functions to open panels
function openLinkPanel(event: Event) {
    const { href } = editor.value?.getAttributes("link") || {}
    linkUrl.value = href ? href : ""
    linkPanel.value?.toggle(event)
}

function openVideoPanel(event: Event) {
    videoPanel.value?.toggle(event)
}

function openVimeoPanel(event: Event) {
    vimeoPanel.value?.toggle(event)
}

function openImagePanel(event: Event) {
    imagePanel.value?.toggle(event)
}

function openLoomPanel(event: Event) {
    loomPanel.value?.toggle(event)
}

// Function to toggle blockquote
function toggleBlockquote() {
    if (editor.value) {
        if (editor.value.isActive("blockquote")) {
            editor.value.chain().focus().toggleBlockquote().run()
        } else {
            editor.value.chain().focus().toggleBlockquote().run()

            // Insert help text after a short delay to indicate how to exit
            setTimeout(() => {
                editor.value
                    ?.chain()
                    .focus()
                    .insertContent([
                        {
                            type: "paragraph",
                            content: [
                                {
                                    type: "text",
                                    text: "Tip: Press Enter twice to exit the quote. Delete this before sending the message.",
                                    marks: [{ type: "italic" }],
                                },
                            ],
                        },
                    ])
                    .run()
            }, 10)
        }
    }
}

// Function to close overlay panels
function closeOverlayPanel(panelName: string) {
    switch (panelName) {
        case "linkPanel":
            linkPanel.value?.hide()
            break
        case "videoPanel":
            videoPanel.value?.hide()
            break
        case "vimeoPanel":
            vimeoPanel.value?.hide()
            break
        case "imagePanel":
            imagePanel.value?.hide()
            break
        case "listingsPanel":
            listingsPanel.value?.hide()
            // If there was a search active, reset it and reload listings
            if (searchQuery.value || listingsStore.tableState.search) {
                searchQuery.value = ""
                listingsStore.tableState.search = ""
                // Reload listings in the background
                listingsStore.getAll().catch((error) => {
                    console.error("Error reloading listings:", error)
                })
            }
            break
        case "textColorPanel":
            textColorPanel.value?.hide()
            break
        case "highlightColorPanel":
            highlightColorPanel.value?.hide()
            break
        case "loomPanel":
            loomPanel.value?.hide()
            break
    }
}

// Function to confirm a link
function confirmLink() {
    if (linkUrl.value) {
        if (!linkUrl.value.startsWith("http://") && !linkUrl.value.startsWith("https://")) {
            linkUrl.value = `https://${linkUrl.value}`
        }
        if (editor.value?.isActive("link")) {
            editor.value.chain().focus().extendMarkRange("link").setLink({ href: linkUrl.value }).run()
        } else {
            editor.value?.chain().focus().setLink({ href: linkUrl.value }).run()
        }
        linkUrl.value = ""
        closeOverlayPanel("linkPanel")
    }
}

// Function to confirm a video embed
function confirmYouTubeVideo() {
    if (videoUrl.value) {
        const isYoutube = videoUrl.value.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/)

        if (isYoutube) {
            const videoId = isYoutube[1]
            const embedUrl = `https://www.youtube.com/embed/${videoId}`

            editor.value
                ?.chain()
                .focus()
                .insertContent([
                    { type: "paragraph" },
                    {
                        type: "youtube",
                        attrs: {
                            src: embedUrl,
                            width: 640,
                            height: 360,
                            controls: true,
                            allowfullscreen: true,
                        },
                    },
                    { type: "paragraph" },
                ])
                .run()
        }

        videoUrl.value = ""
        closeOverlayPanel("videoPanel")
    }
}

function confirmVimeoVideo() {
    if (vimeoUrl.value) {
        // Use the new command from our custom node
        editor.value?.chain().focus().setVimeoVideo({ src: vimeoUrl.value }).run()
        vimeoUrl.value = ""
        closeOverlayPanel("vimeoPanel")
    }
}

function confirmLoomVideo() {
    if (loomUrl.value) {
        editor.value?.chain().focus().setLoomVideo({ src: loomUrl.value }).run()
        loomUrl.value = ""
        closeOverlayPanel("loomPanel")
    }
}

// Add near other ref declarations
const imageLinkUrl = ref<string>("")
const selectedImage = ref<File | null>(null)
const selectedImageUrl = ref<string | null>(null)

// Replace existing functions with these updated versions
function handleImageSelection(event: Event) {
    const input = event.target as HTMLInputElement
    const file = input?.files ? input.files[0] : null
    if (file) {
        selectedImage.value = file
        // Create URL for the image
        const reader = new FileReader()
        reader.onload = (e) => {
            selectedImageUrl.value = e.target?.result as string
        }
        reader.readAsDataURL(file)
    }
}

function clearSelectedImage() {
    selectedImage.value = null
    selectedImageUrl.value = null
    // Reset the file input
    const input = document.querySelector('input[type="file"]') as HTMLInputElement
    if (input) {
        input.value = ""
    }
}

function closeImagePanel() {
    clearSelectedImage()
    imageLinkUrl.value = ""
    closeOverlayPanel("imagePanel")
}

function handleImageWithLink() {
    if (!selectedImage.value || !selectedImageUrl.value || !editor.value) return

    const attrs: { src: string; width: number; height: number; href?: string } = {
        src: selectedImageUrl.value,
        width: 320,
        height: 180,
    }

    if (imageLinkUrl.value) {
        attrs.href = imageLinkUrl.value.startsWith("http") ? imageLinkUrl.value : `https://${imageLinkUrl.value}`
    }

    editor.value.chain().focus().insertContent({ type: "image", attrs }).run()

    // Clear everything and close the panel
    clearSelectedImage()
    imageLinkUrl.value = ""
    closeOverlayPanel("imagePanel")
}

// Function to generate actual HTML email content from listings blocks
function generateEmailHTML(content: string): string {
    // Create a temporary div to parse the content
    const tempDiv = document.createElement("div")
    tempDiv.innerHTML = content

    // Find all video embeds (both YouTube and Vimeo)
    const youtubeEmbeds = tempDiv.querySelectorAll('iframe[src*="youtube.com/embed"]')
    const vimeoContainers = tempDiv.querySelectorAll("[data-vimeo-video]")
    const loomContainers = tempDiv.querySelectorAll("[data-loom-video]")
    const listingsBlocks = tempDiv.querySelectorAll('div[data-type="listings-block"]')

    // Convert YouTube embeds
    youtubeEmbeds.forEach((embed) => {
        const src = embed.getAttribute("src")
        if (src) {
            const videoId = src.split("/").pop()?.split("?")[0]
            if (videoId) {
                const wrapper = document.createElement("div")
                wrapper.setAttribute("data-youtube-video", "")
                wrapper.style.cssText = "max-width: 640px;"

                const link = document.createElement("a")
                link.href = `https://www.youtube.com/watch?v=${videoId}`
                link.target = "_blank"
                link.style.display = "block"

                const thumbnail = document.createElement("img")
                thumbnail.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
                thumbnail.alt = "Watch the video on YouTube"
                thumbnail.style.cssText = "width: 100%; aspect-ratio: 16/9; object-fit: cover; display: block;"

                link.appendChild(thumbnail)
                wrapper.appendChild(link)

                // Replace the entire paragraph containing the iframe if it exists
                const parentParagraph = embed.closest("p")
                if (parentParagraph) {
                    parentParagraph.replaceWith(wrapper)
                } else {
                    embed.replaceWith(wrapper)
                }
            }
        }
    })

    // Convert Vimeo embeds
    vimeoContainers.forEach((container) => {
        // Find the iframe or get the video ID from the existing structure
        const iframe = container.querySelector("iframe")
        let videoId

        if (iframe) {
            const src = iframe.getAttribute("src")
            videoId = src?.split("/").pop()?.split("?")[0]
        } else {
            // If no iframe, try to get video ID from link
            const link = container.querySelector("a")
            if (link) {
                const href = link.getAttribute("href")
                videoId = href?.split("/").pop()
            }
        }

        if (videoId) {
            const wrapper = document.createElement("div")
            wrapper.setAttribute("data-vimeo-video", "")
            wrapper.style.cssText = "max-width: 640px;"

            const link = document.createElement("a")
            link.href = `https://vimeo.com/${videoId}`
            link.target = "_blank"
            link.style.cssText = "display: block; width: 100%; text-decoration: none;"

            const thumbnail = document.createElement("img")
            thumbnail.src = `https://vumbnail.com/${videoId}.jpg`
            thumbnail.alt = "Watch the video on Vimeo"
            thumbnail.style.cssText = "width: 100%; aspect-ratio: 16/9; object-fit: cover; display: block;"

            link.appendChild(thumbnail)
            wrapper.appendChild(link)

            // Replace the entire container
            container.replaceWith(wrapper)
        }
    })

    // Convert Loom embeds
    loomContainers.forEach((container) => {
        // Find the iframe and get the src
        const iframe = container.querySelector("iframe")
        let videoId: string | null = null
        if (iframe) {
            const src = iframe.getAttribute("src")
            // src is like https://www.loom.com/embed/VIDEO_ID or https://www.loom.com/share/VIDEO_ID
            const match = src?.match(/loom.com\/(?:embed|share)\/([a-zA-Z0-9]+)/)
            videoId = match ? match[1] : null
        }
        if (videoId && videoId.includes("?")) {
            videoId = videoId.split("?")[0]
        }
        // Use your custom Loom thumbnail for all Loom videos in emails
        const thumbnailUrl = `${window.location.origin}/thumbnails/LoomFallback.png`
        // If you need a relative path for SSR or backend rendering, use '/thumbnails/Loom.png'

        const wrapper = document.createElement("div")
        wrapper.setAttribute("data-loom-video", "")
        wrapper.style.cssText = "max-width: 640px;"
        const link = document.createElement("a")
        link.href = videoId ? `https://www.loom.com/share/${videoId}` : "https://www.loom.com/"
        link.target = "_blank"
        link.style.display = "block"
        const thumbnail = document.createElement("img")
        thumbnail.src = thumbnailUrl
        thumbnail.alt = "Watch the video on Loom"
        thumbnail.style.cssText = "width: 100%; aspect-ratio: 16/9; object-fit: cover; display: block;"
        // Note: In email clients, JS onerror is not supported, so fallback is handled by always using the logo if videoId is missing
        link.appendChild(thumbnail)
        wrapper.appendChild(link)
        container.replaceWith(wrapper)
    })

    // Convert Listings blocks
    listingsBlocks.forEach((block) => {
        try {
            // Get listings data from the block's attributes
            const listings = block.getAttribute("data-listings")
            if (listings) {
                const listingsData = JSON.parse(listings)
                const listingsHTML = generateListingsEmailHTML(listingsData)
                // Create a wrapper div for the listings HTML
                const wrapper = document.createElement("div")
                wrapper.innerHTML = listingsHTML
                // Replace the original block with the email-friendly version
                block.replaceWith(wrapper)
            }
        } catch (error) {
            console.error("Error converting listings block:", error)
        }
    })

    // Remove any empty paragraphs that might have been left behind
    const emptyParagraphs = tempDiv.querySelectorAll("p:empty")
    emptyParagraphs.forEach((p) => p.remove())

    return tempDiv.innerHTML
}

// Function to generate the actual email HTML for listings
function generateListingsEmailHTML(listings: any[]): string {
    // Helper function to get a value from listing or its attributes
    const getValue = (listing: any, key: string): string => {
        const val = listing[key] || listing.attributes?.[key] || ""
        return String(val)
    }

    let emailHTML = `
    <div style="max-width: 600px; margin: 20px auto; background-color: #ffffff; border-radius: 8px; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1); overflow: hidden; font-family: Arial, sans-serif;">

      <!-- Header -->
      <div style="text-align: center; padding: 30px 20px; background-color: #ffffff;">
        <h1 style="font-size: 2.5em; font-weight: bold; margin: 0 0 10px 0; color: #000;">Featured Properties</h1>
        <p style="color: #6c757d; margin: 0; font-size: 1em;">Discover your next home</p>
      </div>

      <!-- Properties -->`

    listings.forEach((listing) => {
        // Format the property link according to backend pattern
        const state = getValue(listing, "stateOrProvince")
        const city = getValue(listing, "city").replace(/\s+/g, "-")
        const address = getValue(listing, "unparsedAddress").replace(/,/g, "").replace(/\s+/g, "-").concat("-")
        const postalCode = getValue(listing, "postalCode")
        const listingId = getValue(listing, "listingId")
        const cmsUrl = process.env.VUE_APP_CMS_URL
        const agencyDomainId = getDomainId()
        const agencyDomain = agencyStore.agency.agencyDomains.find((domain) => domain._id === agencyDomainId)

        const propertyLink = `https://${agencyDomain?.url.domain}.${cmsUrl}/property-results/${state}/${city}/${address}${postalCode}/${listingId}/`

        // Get image URL with fallback
        const imageUrl =
            listing.media?.[0]?.mediaURL ||
            listing.attributes?.media?.[0]?.mediaURL ||
            require("@/assets/icons/no_image.png")

        // Format address line exactly like backend
        const addressLine = [
            getValue(listing, "streetNumber"),
            getValue(listing, "streetName"),
            getValue(listing, "streetSuffix"),
            listing.city,
            listing.stateOrProvince,
            getValue(listing, "postalCode"),
        ]
            .filter(Boolean)
            .join(" ")

        // Format price exactly like backend
        const formattedPrice = getValue(listing, "listPrice")
            ? `$${Number(getValue(listing, "listPrice")).toLocaleString()}`
            : listing.attributes?.listPrice
            ? `$${listing.attributes.listPrice.toLocaleString()}`
            : "Contact for Price"

        // Status styling exactly like backend
        const statusText = getValue(listing, "standardStatus") || "No Status"
        let statusColor = "#D1FAE5" // Light green
        let statusTextColor = "#065F46" // Dark green

        emailHTML += `
      <div style="margin: 20px; border: 2px solid #d1d5db; border-radius: 8px; overflow: hidden; background-color: #fff;">
        <div style="display: block; text-decoration: none; color: inherit;">
          <img src="${imageUrl}" alt="${addressLine}" style="width: 100%; height: 200px; object-fit: cover; display: block;">
          <div style="padding: 15px 20px;">
            <span style="display: inline-block; background-color: ${statusColor}; color: ${statusTextColor}; padding: 4px 8px; border-radius: 4px; font-size: 0.8em; margin-bottom: 10px; font-weight: 500;">
              ${statusText}
            </span>
            <div style="font-size: 1.5em; font-weight: bold; margin-bottom: 5px; color: #000;">
              ${formattedPrice}
            </div>
            <div style="font-size: 1em; color: #495057;">
              ${addressLine}
            </div>
            <div style="margin-top: 10px;">
              <a href="${propertyLink}" target="_blank" style="background-color: #17a2b8; color: #ffffff; padding: 8px 16px; border-radius: 4px; font-size: 0.9em; font-weight: 500; text-decoration: none; display: inline-block;">
                View Details
              </a>
            </div>
          </div>
        </div>
      </div>`
    })

    emailHTML += `
      <!-- Footer -->
      <div style="text-align: center; padding: 20px; border-top: 1px solid #e9ecef;">
        <div style="margin-bottom: 15px;">
          <div style="margin-bottom: 15px;">
            <a href="#" target="_blank" style="color: #6c757d; text-decoration: none; font-size: 0.9em;">Privacy policy</a>
            <span style="color: #6c757d; margin: 0 8px; font-size: 0.9em;">|</span>
            <a href="#" target="_blank" style="color: #6c757d; text-decoration: none; font-size: 0.9em;">Unsubscribe</a>
          </div>
        </div>
        <hr style="border: none; border-top: 1px solid #e9ecef; margin: 15px auto; width: 80%;">
        <div style="margin-bottom: 15px; font-weight: bold; color: #374151; font-size: 1em;">
          ${agency.value?.agencyName || "GGMS"}
        </div>
        <p style="margin: 0; color: #6c757d; font-size: 0.8em;">
          © ${new Date().getFullYear()} ${agency.value?.agencyName || "GGMS"}. All rights reserved.
        </p>
      </div>
    </div>`

    return emailHTML
}

// Add refs for color pickers
const textColorPanel = ref<OverlayPanel | null>(null)
const highlightColorPanel = ref<OverlayPanel | null>(null)
const selectedTextColor = ref<string>("#000000")
const selectedHighlightColor = ref<string>("#ffeb3b")

// Add color panel functions
const openTextColorPanel = (event: Event) => {
    textColorPanel.value?.toggle(event)
}

const openHighlightColorPanel = (event: Event) => {
    highlightColorPanel.value?.toggle(event)
}

const applyTextColor = () => {
    if (editor.value && selectedTextColor.value) {
        const color = selectedTextColor.value.startsWith("#") ? selectedTextColor.value : `#${selectedTextColor.value}`
        editor.value.chain().focus().setColor(color).run()
        closeOverlayPanel("textColorPanel")
    }
}

const applyTextColorDirect = (color: string) => {
    if (editor.value) {
        editor.value.chain().focus().setColor(color).run()
        closeOverlayPanel("textColorPanel")
    }
}

const clearTextColor = () => {
    if (editor.value) {
        editor.value.chain().focus().unsetColor().run()
        closeOverlayPanel("textColorPanel")
    }
}

const applyHighlightColor = () => {
    if (editor.value && selectedHighlightColor.value) {
        const color = selectedHighlightColor.value.startsWith("#")
            ? selectedHighlightColor.value
            : `#${selectedHighlightColor.value}`
        editor.value.chain().focus().toggleHighlight({ color }).run()
        closeOverlayPanel("highlightColorPanel")
    }
}

const applyHighlightColorDirect = (color: string) => {
    if (editor.value) {
        editor.value.chain().focus().toggleHighlight({ color }).run()
        closeOverlayPanel("highlightColorPanel")
    }
}

const clearHighlightColor = () => {
    if (editor.value) {
        editor.value.chain().focus().toggleHighlight().run()
        closeOverlayPanel("highlightColorPanel")
    }
}

// Add predefined colors
const predefinedColors = [
    "#000000", // Black
    "#4B5563", // Gray
    "#EF4444", // Red
    "#F59E0B", // Yellow
    "#10B981", // Green
    "#3B82F6", // Blue
    "#8B5CF6", // Purple
    "#EC4899", // Pink
]

const predefinedHighlightColors = [
    "#FEF08A", // Light Yellow
    "#BBF7D0", // Light Green
    "#BFDBFE", // Light Blue
    "#DDD6FE", // Light Purple
    "#FBCFE8", // Light Pink
    "#FED7AA", // Light Orange
    "#E5E7EB", // Light Gray
    "#FFFFFF", // White
]
</script>

<style scoped>
/* Toolbar button styles using Tailwind + ::v-deep to target elements inside the editor */
::v-deep .toolbar-button {
    @apply bg-white border border-gray-300 rounded-md p-2 flex items-center justify-center transition-all duration-200 hover:bg-gray-200 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed w-8 h-8;
}

/* Active button styles */
::v-deep .toolbar-button.active {
    @apply bg-blue-200 border-blue-500;
}

/* Editor content styles */
::v-deep .editor-content {
    @apply flex flex-col flex-1 p-4 border border-gray-300 rounded-md shadow-sm min-h-48 cursor-text;
}

::v-deep .editor-content .ProseMirror {
    min-height: 100%;
    flex-grow: 1;
    outline: none; /* Remove any default focus outline */
}

::v-deep .editor-content img {
    max-width: 100%; /* Prevent images from exceeding the container width */
    height: auto; /* Maintain aspect ratio */
    display: block;
}

::v-deep .editor-content iframe {
    @apply w-full h-auto aspect-video;
}

/* Table styles */
::v-deep .editor-content table {
    @apply border-collapse m-0 overflow-hidden table-fixed w-full;
}

::v-deep .editor-content td,
::v-deep .editor-content th {
    @apply border border-gray-300 box-border min-w-[1em] p-1.5 relative align-top;
}

/* Custom styles for property listings table */
::v-deep .editor-content table.property-listings-table {
    border-collapse: separate !important;
    border-spacing: 0 !important;
    background-color: #f8f9fa !important;
    border-radius: 8px !important;
    overflow: hidden !important;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1) !important;
    max-width: 600px !important;
    margin: 20px auto !important;
    width: 100% !important;
}

::v-deep .editor-content table.property-listings-table td,
::v-deep .editor-content table.property-listings-table th {
    border: none !important;
    padding: 0 !important;
    background-color: transparent !important;
}

::v-deep .editor-content table.property-listings-table th {
    background-color: #ffffff !important;
    padding: 30px 20px !important;
    text-align: center !important;
    font-weight: normal !important;
}

::v-deep .editor-content table.property-listings-table tbody td {
    padding: 20px !important;
    background-color: #ffffff !important;
}

::v-deep .editor-content table.property-listings-table tfoot td {
    padding: 20px !important;
    background-color: #ffffff !important;
    text-align: center !important;
}

::v-deep .editor-content td > *,
::v-deep .editor-content th > * {
    @apply mb-0;
}

/* Heading styles */
/* ::v-deep .tiptap h1,
::v-deep .tiptap h2,
::v-deep .tiptap h3,
::v-deep .tiptap h4,
::v-deep .tiptap h5,
::v-deep .tiptap h6 {
    line-height: 1.1;
    margin-top: 2.5rem;
}

::v-deep .tiptap h1,
::v-deep .tiptap h2 {
    margin-top: 3.5rem;
    margin-bottom: 1.5rem;
}

::v-deep .tiptap h1 {
    font-size: 1.4rem;
}

::v-deep .tiptap h2 {
    font-size: 1.2rem;
}

::v-deep .tiptap h3 {
    font-size: 1.1rem;
}

::v-deep .tiptap h4,
::v-deep .tiptap h5,
::v-deep .tiptap h6 {
    font-size: 1rem;
} */

/* Linked text style */
::v-deep .tiptap a {
    @apply text-blue-600 underline hover:text-blue-800 cursor-pointer;
}

/* YouTube Thumbnail Styling */
::v-deep img.youtube-thumbnail {
    position: relative;
    max-width: 100%;
    cursor: pointer;
}

::v-deep img.youtube-thumbnail::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 60px;
    height: 60px;
    margin-top: -30px;
    margin-left: -30px;
    background: url("https://upload.wikimedia.org/wikipedia/commons/7/7e/Youtube_play_button_icon_%282013-2017%29.svg")
        no-repeat center;
    background-size: cover;
    opacity: 0.8;
}

::v-deep .tiptap pre {
    background: #2d2d2d; /* Dark gray background for code blocks */
    border-radius: 0.5rem; /* Rounded corners */
    color: #f8f8f2; /* Lighter text color for contrast */
    font-family: "JetBrainsMono", monospace; /* Monospace font for readability */
    padding: 1rem; /* More padding for larger code blocks */
    margin: 1.5rem 0; /* Separate code blocks from the rest of the content */
    overflow-x: auto; /* Scrollbar for long code lines */
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1); /* Subtle shadow for elevation */
}

/* Enhanced Blockquote Styles */
::v-deep .tiptap blockquote {
    border-left: 4px solid #4f46e5; /* Purple border for blockquote */
    background-color: #f9fafb; /* Light gray background for quote content */
    padding: 1rem; /* Padding inside the blockquote */
    margin: 1.5rem 0; /* Margin to separate from other content */
    font-style: italic; /* Italic text for blockquotes */
    color: #374151; /* Dark gray for readability */
    border-radius: 0.375rem; /* Rounded corners */
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05); /* Subtle shadow for depth */
}

/* List styles */
::v-deep .tiptap ul {
    list-style-type: disc; /* Ensure unordered lists use bullet points */
    list-style-position: outside; /* Position the bullets outside the list item */
    padding-left: 1.5rem; /* Add padding for better spacing */
    margin: 1.25rem 1rem 1.25rem 1rem; /* Adjust margins to give more space */
}

::v-deep .tiptap ol {
    list-style-type: decimal; /* Ensure ordered lists use numbers */
    list-style-position: outside; /* Position the numbers outside the list item */
    padding-left: 1.5rem; /* Add padding for better spacing */
    margin: 1.25rem 1rem 1.25rem 1rem; /* Adjust margins to give more space */
}

::v-deep .tiptap ul li,
::v-deep .tiptap ol li {
    padding-left: 0.5rem; /* Add some padding to list items for better spacing */
}

::v-deep .tiptap ul li p,
::v-deep .tiptap ol li p {
    margin-top: 0.25em;
    margin-bottom: 0.25em;
}

/* Placeholder (at the top) */
::v-deep .tiptap p.is-editor-empty:first-child::before {
    color: #9ca3af; /* Light gray for placeholder text */
    content: attr(data-placeholder);
    float: left;
    height: 0;
    pointer-events: none;
}

/* Modal Styles */
.modal-input {
    @apply mb-4 p-2 border border-gray-300 rounded-md w-full;
    transition: border-color 0.2s;
}
.modal-input:focus {
    @apply border-blue-400 outline-none;
}

/* Modal buttons container */
.modal-buttons {
    @apply flex flex-col gap-2;
}

/* Base styles for modal buttons */
.modal-button {
    @apply px-4 py-2 font-medium text-sm rounded-md transition-all duration-200 shadow-sm focus:outline-none w-full;
}

/* Primary button styles */
.primary-button {
    @apply bg-primary-color-600 text-white hover:bg-primary-color-700 focus:ring focus:ring-blue-300;
}

/* Secondary button styles */
.secondary-button {
    @apply bg-gray-300 text-gray-700 hover:bg-gray-400 focus:ring focus:ring-gray-200;
}

/* Fix dropdown clear button spacing */
::v-deep .p-dropdown .p-dropdown-label {
    padding-left: 2.5rem !important; /* Add extra padding on left for clear button */
    padding-right: 0.5rem !important; /* Reduced padding on right to bring text closer to arrow */
}

::v-deep .p-dropdown .p-dropdown-clear-icon {
    left: 0.75rem !important; /* Position clear button on the left */
    right: auto !important; /* Remove right positioning */
}

/* Add styles for color picker buttons */
::v-deep .p-colorpicker-panel {
    @apply bg-white rounded-md shadow-lg;
}

::v-deep .p-colorpicker-preview {
    @apply w-8 h-8 rounded-md border border-gray-300;
}

/* Update color picker styles */
::v-deep .p-colorpicker {
    @apply w-full;
}

::v-deep .p-colorpicker-preview {
    @apply w-full h-8 rounded-md border border-gray-300 hover:border-gray-400 transition-colors;
}

/* Ensure overlay panels are above other content */
::v-deep .p-overlaypanel {
    z-index: 9999 !important;
}

::v-deep .p-overlaypanel::before,
::v-deep .p-overlaypanel::after {
    z-index: 9999 !important;
}

/* Add active state for color buttons */
.color-button.active {
    @apply ring-2 ring-offset-2 ring-primary-color-500;
}

/* Style the color picker buttons */
.modal-button {
    @apply px-4 py-2 rounded-md text-sm font-medium transition-colors;
    min-width: 80px;
}

.modal-button.primary-button {
    @apply bg-primary-color-600 text-white hover:bg-primary-color-700;
}

.modal-button.warning-button {
    @apply bg-red-100 text-red-700 hover:bg-red-200;
}

.modal-button.secondary-button {
    @apply bg-gray-100 text-gray-700 hover:bg-gray-200;
}

/* Add to the style section */
::v-deep .tiptap [data-loom-video],
::v-deep .tiptap [data-vimeo-video],
::v-deep .tiptap [data-youtube-video] {
    margin: 0 auto;
    display: block;
    width: 100%;
    max-width: 640px;
    aspect-ratio: 16 / 9;
}

::v-deep .tiptap [data-loom-video] iframe,
::v-deep .tiptap [data-vimeo-video] iframe,
::v-deep .tiptap [data-youtube-video] iframe {
    width: 100%;
    height: 100%;
    border: none;
    display: block;
    margin: 0;
}

/* Ensure no extra spacing from paragraphs around videos */
::v-deep .tiptap p:has([data-vimeo-video]),
::v-deep .tiptap p:has([data-youtube-video]) {
    margin: 0;
    padding: 0;
    line-height: 1; /* Collapse the paragraph's line height */
}
</style>
